import React, { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, AlertCircle, FileText } from 'lucide-react';
import { parse } from 'csv-parse/browser/esm/sync';
import { spotifyParserService } from '../../../../backend/services/spotifyParser';
import { supabase } from '../../../../backend/services/supabase';
import type { ParsedSpotifyData, UploadProgress } from '../../../../shared/types/analytics';

export interface SpotifyPrevalidation {
  file: File;
  fileName?: string;
  fileSize?: number;
  valid: boolean;
  errors: string[];
  warnings: string[];
  parsed?: ParsedSpotifyData;
  headers?: string[];
  rows?: Array<Record<string, any>>;
  format?: 'songs' | 'playlists' | 'timeline';
  repairCount?: number;
  repairedLines?: number[];
  skippedCount?: number;
  cleanedCsv?: string;
  recordsCount?: number;
  periodSuggestion?: { end: string; start: string; days: 1|7|28; release?: string };
  source?: 'spotify';
}

interface SpotifySourceCardProps {
  onPrevalidated: (result: SpotifyPrevalidation) => void;
  compact?: boolean;
}

export const SpotifySourceCard: React.FC<SpotifySourceCardProps> = ({ onPrevalidated, compact = false }) => {
  const [busy, setBusy] = useState(false);
  const [lastValidation, setLastValidation] = useState<SpotifyPrevalidation | null>(null);

  const sanitizeWithReport = (input: string): { cleaned: string; repairs: number; lines: number[] } => {
    const delimiter = ',';
    let out = '';
    let inQuotes = false;
    let line = 1;
    let repairs = 0;
    const lines: number[] = [];
    for (let i = 0; i < input.length; i++) {
      const ch = input[i];
      if (ch === '"') {
        const next = input[i + 1];
        if (inQuotes) {
          if (next === '"') {
            out += '""';
            i++;
            continue;
          } else if (next === delimiter || next === '\n' || next === '\r' || next === undefined) {
            inQuotes = false;
            out += ch;
          } else {
            out += '""';
            repairs++;
            if (!lines.includes(line)) lines.push(line);
            continue;
          }
        } else {
          inQuotes = true;
          out += ch;
        }
      } else {
        out += ch;
      }
      if (ch === '\n') line++;
    }
    return { cleaned: out, repairs, lines };
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return;
    const file = acceptedFiles[0];
    setBusy(true);
    try {
      const text = await file.text();
      const { cleaned: safe, repairs, lines } = sanitizeWithReport(text);
      const validation = spotifyParserService.validateSpotifyCSV(safe);
      // Extract CSV headers
      const firstLine = safe.split('\n')[0] || '';
      const cleanFirst = firstLine.replace(/^\uFEFF/, ''); // strip BOM
      const headers = cleanFirst.split(',').map((h) => h.replace(/^\"|\"$/g, '').trim());
      const lower = headers.map(h => h.toLowerCase());
      // Header-based format detection
      let headerFormat: 'songs' | 'playlists' | 'timeline' = 'songs';
      if (lower.includes('date') && lower.includes('streams')) headerFormat = 'timeline';
      else if (lower.includes('playlist name') || (lower.includes('title') && lower.includes('author')) || lower.includes('owner')) headerFormat = 'playlists';

      let parsed: ParsedSpotifyData | undefined = undefined;
      let rows: any[] | undefined = undefined;
      if (validation.valid) {
        try {
          parsed = await spotifyParserService.parseSpotifyCSV(safe, file.name);
          rows = parse(safe, { columns: true, skip_empty_lines: true, trim: true, relax_quotes: true, relax_column_count: true, skip_lines_with_error: true, bom: true });
        } catch (e) {
          // Fallback: if parsing fails despite validation, surface error
          validation.errors.push('Failed to parse CSV for preview');
        }
      }

      // Records + skipped rows
      let recordsCount = (parsed?.songs?.length || 0) + (parsed?.playlists?.length || 0) || (rows?.length || 0);
      // Approximate skipped rows: data lines minus parsed rows
      const totalDataLines = safe.split('\n').filter((l, i) => i > 0 && l.trim()).length;
      const skipped = rows ? Math.max(0, totalDataLines - rows.length) : 0;

      // Period suggestion for songs/playlists; for timeline we'll compute from content and release date
      let periodSuggestion: { end: string; start: string; days: 1|7|28 } | undefined;
      if (['songs','playlists'].includes(headerFormat)) {
        const name = file.name.toLowerCase();
        let days: 1|7|28 = 1;
        if (name.includes('7day') || name.includes('7-day') || name.includes('weekly')) days = 7;
        else if (name.includes('28day') || name.includes('28-day') || name.includes('monthly')) days = 28;
        const end = new Date(file.lastModified);
        end.setDate(end.getDate() - 1);
        const start = new Date(end);
        start.setDate(start.getDate() - (days - 1));
        periodSuggestion = { end: end.toISOString().split('T')[0], start: start.toISOString().split('T')[0], days };
      }

      // For timeline: compute date range from content and attempt to align to release date
      if (headerFormat === 'timeline' && parsed?.songs?.length) {
        // compute min/max from parsed songs
        const dates = parsed.songs.map(s => s.recorded_at).filter(Boolean).sort();
        let start = dates[0];
        let end = dates[dates.length - 1];
        // Try to infer release date from filename and filter to >= (release - 1 day)
        const songFromName = file.name.replace(/[-_]timeline\.csv$/i, '').replace(/[-_]/g, ' ').trim();
        let releaseFound: string | undefined;
        try {
          if (songFromName) {
            const { data } = await supabase
              .from('releases')
              .select('release_date')
              .ilike('title', songFromName);
            let rdate = data && data[0]?.release_date;
            if (!rdate) {
              // fallback: look into spotify_song_metrics if available
              const { data: sm } = await supabase
                .from('spotify_song_metrics')
                .select('release_date')
                .ilike('song_name', songFromName)
                .not('release_date', 'is', null)
                .limit(1);
              rdate = sm && sm[0]?.release_date;
            }
            if (rdate) {
              releaseFound = rdate;
              const cutoff = new Date(rdate);
              cutoff.setDate(cutoff.getDate() - 1);
              const cutoffStr = cutoff.toISOString().split('T')[0];
              // filter parsed songs and rows
              parsed.songs = parsed.songs.filter(s => s.recorded_at >= cutoffStr);
              if (rows) {
                rows = rows.filter(r => (r.date || r.Date) >= cutoffStr);
              }
              // recompute dates and records
              const ndates = parsed.songs.map(s => s.recorded_at).filter(Boolean).sort();
              if (ndates.length) {
                start = ndates[0];
                end = ndates[ndates.length - 1];
                recordsCount = parsed.songs.length;
              }
            }
          }
        } catch {}
        // If still no release date, infer from first non-zero streams
        if (!releaseFound) {
          const firstNonZero = parsed.songs.find(s => (s.streams ?? 0) > 0);
          if (firstNonZero?.recorded_at) {
            const cutoff = new Date(firstNonZero.recorded_at);
            cutoff.setDate(cutoff.getDate() - 1);
            const cutoffStr = cutoff.toISOString().split('T')[0];
            parsed.songs = parsed.songs.filter(s => s.recorded_at >= cutoffStr);
            if (rows) rows = rows.filter(r => (r.date || r.Date) >= cutoffStr);
            const ndates = parsed.songs.map(s => s.recorded_at).filter(Boolean).sort();
            if (ndates.length) {
              start = ndates[0];
              end = ndates[ndates.length - 1];
              recordsCount = parsed.songs.length;
            }
            releaseFound = firstNonZero.recorded_at;
          }
        }
        // set suggestion for UI
        periodSuggestion = { end, start, days: 1, release: releaseFound } as any;
      }

      const result: SpotifyPrevalidation = {
        source: 'spotify',
        file,
        fileName: file.name,
        fileSize: file.size,
        valid: validation.valid,
        errors: validation.errors,
        warnings: validation.warnings,
        parsed,
        headers,
        rows,
        format: headerFormat,
        repairCount: repairs,
        repairedLines: lines,
        skippedCount: skipped,
        recordsCount,
        periodSuggestion
      };
      setLastValidation(result);
      onPrevalidated(result);
    } finally {
      setBusy(false);
    }
  }, [onPrevalidated]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    maxSize: 50 * 1024 * 1024,
    multiple: false
  });

  return (
    <div className={`${compact ? 'p-2' : 'p-4'} rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900`}>
      {!compact && (
        <div className="mb-3 flex items-center gap-2">
          <img src="/brand/spotify_logo.svg" alt="Spotify" className="w-5 h-5" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Upload data</span>
        </div>
      )}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg ${compact ? 'p-2' : 'p-6'} text-center cursor-pointer transition-colors ${
          isDragActive && !isDragReject
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
            : isDragReject
            ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
      >
        <input {...getInputProps()} />
        <div className={`${compact ? 'mb-1' : 'mb-2'} flex justify-center`}>
          {isDragReject ? (
            <AlertCircle className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} text-red-500`} />
          ) : (
            <Upload className={`${compact ? 'w-5 h-5' : 'w-8 h-8'} text-gray-400`} />
          )}
        </div>
        <div className={`${compact ? 'text-xs' : 'text-sm'} text-gray-700 dark:text-gray-300`}>
          {busy ? 'Reading file…' : isDragActive ? (isDragReject ? 'Invalid file type' : 'Drop files here') : 'Drag & drop or click to browse'}
        </div>
        {!compact && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Max 50MB</div>}
      </div>

      {!compact && lastValidation && (
        <div className="mt-3 text-xs">
          <div className="flex items-center gap-2">
            <FileText className="w-3 h-3 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">{lastValidation.file.name}</span>
          </div>
          {!lastValidation.valid && lastValidation.errors.length > 0 && (
            <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
              <div className="font-medium text-red-700 dark:text-red-300 mb-1">Validation Errors</div>
              <ul className="list-disc pl-5 text-red-700 dark:text-red-300">
                {lastValidation.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
          {lastValidation.warnings.length > 0 && (
            <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
              <div className="font-medium text-amber-800 dark:text-amber-200 mb-1">Warnings</div>
              <ul className="list-disc pl-5 text-amber-800 dark:text-amber-200">
                {lastValidation.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
